/**
 * Vendor evaluation (docs/24 §3.6).
 *
 * Nine criteria from the kick-off notes, a weight each, a score out of five
 * per criterion, and a weighted total out of a hundred that grades the
 * supplier. Five of the nine the system can measure from what has already
 * happened — receipts against promised dates, inspection results, quotations
 * won, the supplier's own paperwork, the credit it gives. The other four are a
 * buyer's judgement, and are asked for rather than guessed.
 */

export const VENDOR_MEASURES = [
  'MANUAL', 'QUALITY', 'PRICING', 'DELIVERY', 'COMPLIANCE', 'FINANCIAL', 'PAST_PERFORMANCE',
] as const
export type VendorMeasure = (typeof VENDOR_MEASURES)[number]

export const isVendorMeasure = (v: unknown): v is VendorMeasure =>
  typeof v === 'string' && (VENDOR_MEASURES as readonly string[]).includes(v)

export const VENDOR_MEASURE_OPTIONS: ReadonlyArray<[VendorMeasure, string]> = [
  ['MANUAL', 'Scored by the evaluator'],
  ['QUALITY', 'Measured — accepted at inspection'],
  ['PRICING', 'Measured — quotations won on price'],
  ['DELIVERY', 'Measured — delivered on time'],
  ['COMPLIANCE', 'Measured — registrations and licences'],
  ['FINANCIAL', 'Measured — credit period'],
  ['PAST_PERFORMANCE', 'Measured — orders and debit notes'],
]

export interface CriterionDefault {
  readonly code: string
  readonly name: string
  readonly weight: number
  readonly measure: VendorMeasure
}

/** The notes' own list. Seeded as the master; used as-is where it is empty. */
export const DEFAULT_VENDOR_CRITERIA: readonly CriterionDefault[] = [
  { code: 'QUALITY', name: 'Quality of the product', weight: 20, measure: 'QUALITY' },
  { code: 'PRICING', name: 'Pricing and cost competitiveness', weight: 15, measure: 'PRICING' },
  { code: 'DELIVERY', name: 'Delivery performance', weight: 15, measure: 'DELIVERY' },
  { code: 'COMPLIANCE', name: 'Compliance and documentation', weight: 10, measure: 'COMPLIANCE' },
  { code: 'FINANCIAL', name: 'Financial stability', weight: 10, measure: 'FINANCIAL' },
  { code: 'SERVICE', name: 'Customer service and support', weight: 10, measure: 'MANUAL' },
  { code: 'TECHNICAL', name: 'Technical capability', weight: 10, measure: 'MANUAL' },
  { code: 'RISK', name: 'Risk and reliability', weight: 5, measure: 'MANUAL' },
  { code: 'HISTORY', name: 'Past performance', weight: 5, measure: 'PAST_PERFORMANCE' },
]

export const VENDOR_GRADES = ['A', 'B', 'C', 'D'] as const
export type VendorGrade = (typeof VENDOR_GRADES)[number]

/** What a grade means to the person choosing a supplier. */
export const GRADE_MEANING: Readonly<Record<VendorGrade, string>> = {
  A: 'Preferred',
  B: 'Approved',
  C: 'Conditional — watch closely',
  D: 'Not recommended',
}

/** Out of 100: 80 and over is A, 60 B, 40 C, anything less D. */
export const gradeFor = (score: number): VendorGrade =>
  score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : 'D'

/** A band of a measured figure, read as a score out of five. Highest band first. */
export const bandScore = (value: number, bands: readonly [number, number, number, number]): number =>
  value >= bands[0] ? 5 : value >= bands[1] ? 4 : value >= bands[2] ? 3 : value >= bands[3] ? 2 : 1

export const VENDOR_TYPES: ReadonlyArray<[string, string]> = [
  ['MANUFACTURER', 'Manufacturer'],
  ['DEALER', 'Authorised dealer'],
  ['TRADER', 'Trader'],
  ['SERVICE', 'Service provider'],
  ['CONTRACTOR', 'Contractor'],
]

export const APPROVAL_STATUSES: ReadonlyArray<[string, string]> = [
  ['APPROVED', 'Approved — buy from them'],
  ['PROVISIONAL', 'Provisional — each order needs an approval'],
  ['BLOCKED', 'Blocked — do not buy'],
]
